import React from 'react';
import { useFollowActiveNav } from '@/hooks/useFollowActiveNav';
import { withGroupOpen } from '@/lib/followActiveNav';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  PanelLeftClose,
  LogOut,
  ChevronDown,
  ChevronRight,
  Star,
  Search,
  Pencil,
  MoreHorizontal,
} from 'lucide-react';
import { useAlertCounts } from '@/hooks/useAlertCounts';
import { alertBadgeText } from '@/hooks/alertCounts';
import { useApi } from '@/hooks/useApi';
import { fetchDemandRequestPendingCount } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useWorkspace } from '@/lib/workspace';
import { SectionNav, SectionNavGroup } from './SectionNav';
import { SuryaMark } from '../brand/SuryaMark';
import { VIEW_REGISTRY, type RegistryPage } from '@/lib/viewRegistry';
import {
  NAV_GROUP_ORDER,
  NAV_GROUP_PAGES,
  STANDALONE_PATHS,
  FALLBACK_GROUP,
  FAVOURITES_GROUP,
  searchPages,
  parseFavourite,
  favouriteLabel,
} from '@/config/nav-groups';
// SPEC-563 — the per-user layout: the pure model and the one shared copy of it.
import {
  FAVOURITES_ID,
  NEW_ID,
  MAX_NAV_FOLDERS,
  cleanLabel,
  deleteFolder,
  fileNewPages,
  hide,
  makeFolderId,
  moveEntry,
  moveFolder,
  newFolder,
  nudgeEntry,
  render as renderLayout,
  renameFolder,
  show,
  standardFolderId,
  favouriteOp,
  moveDestinations,
  SECTIONS_ID,
  fileEntry,
  followTarget,
  offeredSections,
  setFavourite,
  dropZone,
  planDrop,
  type DragItem,
  type DropRefusal,
  type DropTarget,
  type DropZone,
  type Nudge,
} from '@/config/nav-layout';
import { useNavLayout } from '@/hooks/useNavLayout';

// SPEC-509: group-heading marks, and the phone foot rows (Alerts, Walk-throughs, Feedback).
import { GroupMark } from '../brand/marks/Mark';
import type { GroupMarkId } from '../brand/marks/markData';
import { RailFootRows } from './RailFootRows';
// SPEC-535: the one nav size scale, and the pure "opens further" rules.
import { NAV_SCALE, opensFurther, tabsExpanded } from '@/config/nav-scale';

type TabStatus = 'active' | 'development' | 'future';

interface NavItem {
  name: string;
  href: string;
  alertKey: 'supplyChain' | 'supplierRisk' | 'sales' | null;
  status: TabStatus;
  subGroups?: SectionNavGroup[];
}

/** Flat allowed-key list for a nav item after workspace + user filtering (SPEC-057). */
function allowedSubKeys(
  item: NavItem,
  workspaceTabs?: string[],
  userTabs?: string[],
): string[] | undefined {
  if (!item.subGroups) return undefined;
  let keys = item.subGroups.flatMap((g) => g.items.map((i) => i.key));
  if (workspaceTabs) keys = keys.filter((k) => workspaceTabs.includes(k));
  if (userTabs) keys = keys.filter((k) => userTabs.includes(k));
  return keys;
}

// SPEC-139: the page→group→tab STRUCTURE (keys, labels, advanced flags) is no
// longer written here — it lives in config/view-registry.json, the one file the
// backend registry also reads (backend/view_registry.py). This file owns only
// PRESENTATION: which icon/status/alert badge a page gets, and the honest
// data-source tooltips. NAV_GROUPS/STANDALONE below are assembled from the
// registry at module load, so the nav can never drift from the registry again
// (that is why SPEC-139 deleted the old regex guard).
//
// SPEC-452: the top-level GROUPING left this record. Grouping is the nav's
// information architecture, not presentation, so it lives in config/nav-groups.ts
// where nav-groups.test.ts asserts it against the registry page by page.
//
// SPEC-456: the leading page ICON left this record too, and the nav renders none.
// Param's reading of the live nav was that the rows "should be just sort of single
// lines rather than having the icons on them... it's not consistent", and the audit
// agreed on three counts:
//   1. The glyphs carried no information. Five of the 26 pages SHARED an icon
//      (Boxes on Material Requirements + Box A, BarChart3 on Demand Planning +
//      Forecast Workbench, Truck on Supply Chain + Routes, BadgePercent on
//      Promotions + Deductions, Activity on Regime Alerts + Analytics), so the
//      column could not be learned even in principle.
//   2. Nothing needs them. The collapsed sidebar is `w-0` — there is no icon-only
//      rail anywhere in this app, which is the usual reason a nav keeps icons.
//      (SPEC-462 added a rail — AppRail — but it carries ~6 GLOBAL verbs, never pages,
//      so this reasoning still stands: page rows stay icon-free.)
//   3. They broke the one grammar this spec is for. Sub-view rows (SectionNav) have
//      never had icons, and the Favourites group now mixes page rows with pinned
//      sub-view rows; an icon on half the rows is the inconsistency, not the fix.
// The status dot and the alert badge stay — those two DO carry information.
interface PageMeta {
  alertKey: NavItem['alertKey'];
  status: TabStatus;
}

const PAGE_META: Record<string, PageMeta> = {
  '/': { alertKey: null, status: 'development' },
  // SPEC-466 — the agent findings by remit ("What needs a look").
  '/alerts': { alertKey: null, status: 'active' },
  // Order here carries no meaning — render order comes from config/nav-groups.ts.
  // Kept in view-registry.json order for readability only.
  '/demand-planning': { alertKey: null, status: 'active' },
  '/material-requirements': { alertKey: null, status: 'active' },
  '/analogue-matching': { alertKey: null, status: 'development' },
  '/production-upload': { alertKey: null, status: 'active' },
  '/inventory-targets': { alertKey: null, status: 'active' },
  '/forecast-benchmark': { alertKey: null, status: 'active' },
  // SPEC-434 — V7 promotional engine read-layer pages (DARK preview). Development status
  // dot: they READ the real /api/promo/* numbers but the engine writes nothing live yet.
  '/box-a': { alertKey: null, status: 'development' },
  '/forecast-workbench': { alertKey: null, status: 'development' },
  '/promotions': { alertKey: null, status: 'development' },
  '/replenishment-detail': { alertKey: null, status: 'active' },
  '/regime-alerts': { alertKey: null, status: 'active' },
  '/supply-chain': { alertKey: 'supplyChain', status: 'active' },
  '/routes': { alertKey: null, status: 'development' },
  '/sales': { alertKey: 'sales', status: 'development' },
  '/customer-dormancy': { alertKey: null, status: 'active' },
  '/intelligence': { alertKey: null, status: 'development' },
  '/reports': { alertKey: null, status: 'future' },
  '/supplier-risk': { alertKey: 'supplierRisk', status: 'future' },
  '/retro-billing': { alertKey: null, status: 'future' },
  '/deductions': { alertKey: null, status: 'development' },
  '/roadmap': { alertKey: null, status: 'active' },
  '/products': { alertKey: null, status: 'development' },
  '/governance': { alertKey: null, status: 'active' },
  // SPEC-448 — the policy/method/sign-off/audit half of the old Governance page.
  '/methodology': { alertKey: null, status: 'active' },
  // SPEC-455 — Demand Source: live read-only diagnostics of which demand number is in use.
  '/demand-source': { alertKey: null, status: 'active' },
  // SPEC-503 — Docs: how the platform works, page by page, kept current by the docs guard.
  '/docs': { alertKey: null, status: 'active' },
  // SPEC-541 — Recommendations and outcomes: every suggestion, what the team did, what happened.
  '/recommendations': { alertKey: null, status: 'active' },
  '/admin': { alertKey: null, status: 'future' },
  '/super-admin': { alertKey: null, status: 'future' },
};

// Honest data-source blurbs (hover), keyed `${path}:${tabKey}`. Presentation
// only — absence just means no tooltip. Kept for the tabs that had them under
// SPEC-112/137; new sections can gain blurbs here without touching structure.
const TAB_TOOLTIPS: Record<string, string> = {
  '/demand-planning:morning': 'Your day at a glance — what changed, what needs a decision, and whether we are winning',
  '/demand-planning:amendments': 'Planner amendments with reason codes, auto-expiry, and FVA (did the change help?) tracking',
  '/demand-planning:forecast': 'Per-SKU forecasts from live portal orders (2017 → today). Base statistical model tournament, per-class WAPE thresholds.',
  '/demand-planning:classification': 'Demand classification from live portal orders (130-week window). ADI/CV² Syntetos-Boylan method.',
  '/demand-planning:psr-console': 'One console: exception queue → per-SKU weekly projection, rolling forward cover, buy recommendation and reasoning. The consolidation of Planning & Schedule, Netting and Replenishment (SPEC-231).',
  '/demand-planning:promotions': 'Promotional calendar from Sudhin — live entries with margin and uplift economics per SKU',
  '/demand-planning:scenarios': 'What-if scenarios computed from live stock & velocity (supplier delay, demand spike, seasonal readiness) — plus suggest your own',
  '/demand-planning:po-approval': 'Purchase orders awaiting a decision — ERP proposals + portal PO requests',
  '/demand-planning:deficit': 'Forecast-driven deficit projection: weekly stock position from our forecast + scheduled inbound. Generated by the system, not mirrored from the ERP (G13).',
  '/demand-planning:stock-cover': 'Live warehouse stock (updated 4-hourly) + velocity from live orders',
  '/demand-planning:bbe-risk': 'Best Before End dates from live warehouse stock (updated 4-hourly)',
  '/demand-planning:warehouses': 'Stock by warehouse site (live feed)',
  '/demand-planning:pilot': 'The measurement pack — the working set, daily exceptions & the weekly scorecard',
  '/demand-planning:forecast-vs-actuals': 'What the forecast said at the time (as filed) vs what actually happened, for any past week — plus the buy decision recorded that week. Downloadable.',
  '/demand-planning:overview': 'Demand overview from order and invoice history',
  '/demand-planning:calculations': 'Full transparency — see models, formulas, and numbers for any SKU or customer',
  '/demand-planning:pipeline-runs': 'Forecast pipeline run history, bias drift checks, and decision ledger',
};

/** Registry groups → SectionNav groups, injecting presentation tooltips.
 *  Empty-label groups (flat pages) pass straight through — SectionNav renders
 *  them without a header. */
function toSubGroups(page: RegistryPage): SectionNavGroup[] | undefined {
  if (!page.groups) return undefined;
  return page.groups.map((g) => ({
    label: g.label,
    items: g.tabs.map((t) => ({
      key: t.key,
      label: t.label,
      advanced: t.advanced,
      tooltip: TAB_TOOLTIPS[`${page.path}:${t.key}`],
    })),
  }));
}

function toNavItem(page: RegistryPage): NavItem {
  // Promotion review: a registry page missing its PAGE_META entry must degrade
  // (no badge, in-development dot), never throw at module load — an undefined
  // `meta` here crashed the whole app boot in production, where the dev warn is
  // stripped.
  const meta = PAGE_META[page.path] ?? { alertKey: null, status: 'development' as const };
  return {
    name: page.label,
    href: page.path,
    alertKey: meta.alertKey,
    status: meta.status,
    subGroups: toSubGroups(page),
  };
}

/** One NavItem per registry page, built once. Shared by the groups, the Favourites
 *  group and the search results, so a page pinned AND grouped is the same object. */
const NAV_ITEMS: Map<string, NavItem> = new Map(
  VIEW_REGISTRY.map((p) => [p.path, toNavItem(p)]),
);

/** SPEC-509 — the sidebar lockup, as the Overview link when the login can see Overview.
 *  Named by its CONTENT (wordmark + tagline) plus a visually hidden ", go to Overview": WCAG
 *  2.5.3 (label in name) needs every visible word inside the accessible name, so a
 *  speech-input user can say what they see. The rail mark has no visible words: "Overview". */
function HomeLockup({ enabled, onNavigate, children }: { enabled: boolean; onNavigate?: () => void; children: React.ReactNode }) {
  if (!enabled) return <div className="flex items-center gap-2.5 min-w-0">{children}</div>;
  return (
    <NavLink
      to="/"
      end
      onClick={onNavigate}
      className="flex items-center gap-2.5 min-w-0 rounded-lg -m-1 p-1 hover:bg-[var(--sidebar-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/60 transition-colors"
    >
      {children}
      <span className="sr-only">, go to Overview</span>
    </NavLink>
  );
}

// SPEC-509: Overview and Alerts (STANDALONE_PATHS) are no longer rows in the list. Overview
// is the brand mark (rail + the logo above), Alerts sits in the rail's foot cluster (phones:
// the foot rows below). Both stay in page search and can still be pinned to Favourites.

// SPEC-452 — registry pages that config/nav-groups.ts does not list. They are appended
// to the fallback group: an omission must put the page in the WRONG place, never hide it
// (a hidden page is how Sudhin lost two screens in Sep 2026). nav-groups.test.ts fails CI
// on the omission, so this path exists for the running app, not as a licence to skip config.
const CONFIGURED_PATHS = new Set(NAV_GROUP_ORDER.flatMap((label) => NAV_GROUP_PAGES[label]));
const UNGROUPED_PATHS: string[] = VIEW_REGISTRY
  .map((p) => p.path)
  .filter((path) => !STANDALONE_PATHS.includes(path) && !CONFIGURED_PATHS.has(path));

// SPEC-563: the verb groups are now the STANDARD LAYOUT's folders, built once in
// hooks/useNavLayout.ts (STANDARD_GROUPS) with the same fallback rule: `groupOf` decides
// where an unlisted page lands, so it is shown in the wrong place rather than hidden.

/** The group mark a heading carries: the verb mark on a standard folder that still has its
 *  standard name, the star mark on Favourites, none on a folder the user made or renamed
 *  (or on New) — a verb mark on "Morning" would claim a meaning the folder does not have. */
const STANDARD_MARKS: Record<string, { label: string; mark: GroupMarkId }> = Object.fromEntries(
  NAV_GROUP_ORDER.map((label) => [standardFolderId(label), { label, mark: label as GroupMarkId }]),
);
function markFor(id: string, label: string): GroupMarkId | null {
  if (id === FAVOURITES_ID) return 'Favourites';
  const std = STANDARD_MARKS[id];
  return std && std.label === label ? std.mark : null;
}

// Two pairings can still drift, so warn loudly in dev (stripped from the production build):
// a registry page with no PAGE_META entry (renders with a default status dot) and a registry
// page with no group entry (renders in the fallback group).
if (import.meta.env?.DEV) {
  const noMeta = VIEW_REGISTRY.filter((p) => !PAGE_META[p.path]).map((p) => p.path);
  if (noMeta.length) {
    console.warn(
      `[Sidebar] view-registry pages missing a PAGE_META entry (no badge, in-development dot): ${noMeta.join(', ')}`,
    );
  }
  if (UNGROUPED_PATHS.length) {
    console.warn(
      `[Sidebar] view-registry pages missing a nav group in config/nav-groups.ts (shown under ${FALLBACK_GROUP}): ${UNGROUPED_PATHS.join(', ')}`,
    );
  }
}

/** One resolved, access-checked row of a layout group (SPEC-563; was the SPEC-456 pin row).
 *  The entry format and the label/omission rules are pure, so they live in
 *  config/nav-groups.ts and config/nav-layout.ts where their tests assert them. */
interface ResolvedEntry {
  /** The STORED string. This is the row's identity when moving, hiding or starring. */
  entry: string;
  item: NavItem;
  /** Absent for a whole-page row. */
  tabKey?: string;
  /** "Demand Planning" or "Demand Planning · Forecast" — one wording. */
  label: string;
}

interface ResolvedGroup {
  id: string;
  label: string;
  rows: ResolvedEntry[];
  /** The stored entries this login can see in this group, in order (for Move up/down). */
  visible: string[];
}

/** A row in edit mode: the group it sits in and the row itself. */
interface EditTarget {
  group: ResolvedGroup;
  row: ResolvedEntry;
}

// SPEC-563 (D4): collapse state stays on the DEVICE, keyed by folder id so a rename keeps it.
// A new key: the SPEC-452 key was keyed by group label.
const LS_KEY = 'suryaiq_sidebar_folders';

function loadGroupState(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveGroupState(state: Record<string, boolean>) {
  localStorage.setItem(LS_KEY, JSON.stringify(state));
}

interface SidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
  onNavigate?: () => void;
  /** SPEC-462: bumped by the rail's search button. Each change focuses the search box. */
  searchFocusToken?: number;
}

/** SPEC-288: planner-tier gate for the demand-request pending badge. Mirrors the backend
 * `planner_tier_denied(user,'pilot')` and `DemandPlanningPage._isPlannerTier` so the badge
 * renders exactly where the Decide column and the pending-count API allow it — never for
 * exec_result C-suite or team tiers. Kept in lockstep with those two (coherence principle). */
function _sidebarIsPlannerTier(
  user?: { role?: string; tabs?: Record<string, string[]> | null } | null,
): boolean {
  if (!user) return false;
  if (user.role === 'team' || user.role === 'sc_director') return false;   // fail-closed, non-planner
  if (user.role === 'admin' || user.role === 'super_admin') return true;
  const tabs = user.tabs;
  if (!tabs) return true;                                                   // unrestricted (Sudhin)
  const allowed = tabs['/demand-planning'];
  if (allowed == null) return true;                                         // page not tab-restricted
  return Array.isArray(allowed) && allowed.includes('pilot');
}

export function Sidebar({ collapsed, onToggle, onNavigate, searchFocusToken }: SidebarProps) {
  const alertCounts = useAlertCounts();
  const { user, logout } = useAuth();
  // SPEC-288: open demand-requests waiting on a planner decision — the sidebar badge.
  // Fetched only for planner-tier users (the endpoint 403s others); polls with the nav.
  const plannerTier = _sidebarIsPlannerTier(user);
  const { data: pendingData } = useApi(
    fetchDemandRequestPendingCount,
    [],
    { enabled: plannerTier, refreshInterval: 60000 },
  );
  const demandRequestPending = plannerTier ? (pendingData?.pending_count || 0) : 0;
  const { workspace } = useWorkspace();
  const location = useLocation();
  const activeSubKey = new URLSearchParams(location.search).get('tab') || '';

  // Group expand/collapse, keyed by group id — absent means expanded (the default).
  const [groupState, setGroupState] = React.useState<Record<string, boolean>>(() => loadGroupState());

  const openGroup = (label: string) => {
    setGroupState((prev) => {
      const next = withGroupOpen(prev, label);
      if (next !== prev) saveGroupState(next);
      return next;
    });
  };

  const toggleGroup = (label: string) => {
    setGroupState((prev) => {
      const next = { ...prev, [label]: !prev[label] };
      saveGroupState(next);
      return next;
    });
  };

  const rolePages = user?.pages || ['/'];
  const allowedPages = workspace?.pages
    ? rolePages.filter((p: string) => workspace.pages!.includes(p))
    : rolePages;

  /* ── SPEC-563 — the per-user layout ───────────────────────────────────────────
   * One shared copy for both Sidebar instances, the rail menu and the Arrange dialog
   * (hooks/useNavLayout.ts holds the save races: serialised, newest-wins, the in-flight GET,
   * and the 409 from another tab). The star is now one op on that layout (D3): it adds the
   * entry to, or removes it from, the Favourites folder.
   * The phone overlay (the instance given `onNavigate`) is READ-ONLY (D10): no pencil, no
   * "…" menus. */
  const nav = useNavLayout();
  const favourites = nav.layout.favourites;
  const readOnly = Boolean(onNavigate);
  // SPEC-563 fix loop 2 (Codex finding 2): the star records an INTENT decided from what is on
  // screen ("star it" / "un-star it"), never a toggle. A save that 409s replays its ops on the
  // other tab's newer layout, and a replayed toggle would undo the same star made there.
  const toggleFavourite = (entry: string) => nav.apply(favouriteOp(nav.layout, entry));

  /** Tab keys of THIS page that are pinned — drives the stars on its sub-view rows. */
  const pinnedTabsFor = (path: string): string[] => {
    const keys: string[] = [];
    for (const entry of favourites) {
      const parsed = parseFavourite(entry);
      if (parsed && parsed.path === path && parsed.tabKey) keys.push(parsed.tabKey);
    }
    return keys;
  };

  /* SPEC-563 — the layout, resolved to renderable groups. ACCESS: every entry passes
   * through `render` (config/nav-layout.ts) with this login's pages (role ∩ view ∩
   * workspace, in registry order), the sub-views the same workspace/user filtering allows,
   * and `locked` (the access-tag hook). A stored entry never opens a page by itself
   * (NK-52); an allowed page filed nowhere shows in "New" (never lost). `favouriteLabel`
   * then owns the wording and drops anything it cannot name (SPEC-456). */
  // The CONTENT of the tab-restriction maps is what this memo reads, not their identity —
  // same reason the search memo below keys on `allowedPages.join('|')`.
  const tabsKey = JSON.stringify([workspace?.tabs ?? null, user?.tabs ?? null]);
  const layoutGroups = React.useMemo<ResolvedGroup[]>(() => {
    const pages = VIEW_REGISTRY.filter((p) => allowedPages.includes(p.path));
    const allowedTabs: Record<string, string[] | undefined> = {};
    for (const p of pages) {
      const item = NAV_ITEMS.get(p.path);
      if (item) allowedTabs[p.path] = allowedSubKeys(item, workspace?.tabs?.[p.path], user?.tabs?.[p.path]);
    }
    const groups = renderLayout(nav.layout, {
      allowedPages: pages.map((p) => p.path),
      allowedTabs,
      locked: nav.locked,
      standalone: STANDALONE_PATHS,
    });
    return groups.map((g) => {
      const rows: ResolvedEntry[] = [];
      for (const entry of g.items) {
        const target = parseFavourite(entry);
        const item = target ? NAV_ITEMS.get(target.path) : undefined;
        if (!target || !item) continue;
        const label = favouriteLabel(entry, pages, allowedTabs[target.path]);
        if (label === null) continue;
        rows.push({ entry, item, tabKey: target.tabKey, label });
      }
      return { id: g.id, label: g.id === FAVOURITES_ID ? FAVOURITES_GROUP : g.label, rows, visible: rows.map((r) => r.entry) };
    });
    // allowedPages is rebuilt each render; its CONTENT is what matters here.
  }, [nav.layout, nav.locked, allowedPages.join('|'), tabsKey]);
  const shownFavourites = layoutGroups.find((g) => g.id === FAVOURITES_ID)?.visible ?? [];
  const folderGroups = layoutGroups.filter((g) => g.id !== FAVOURITES_ID && g.id !== NEW_ID);

  /* ── SPEC-563 — edit mode (M2b): the menu + keyboard path for EVERY operation ────
   * The pencil beside the search box turns it on (desktop instance only). Each row and
   * folder heading then carries a "…" button: a DISCLOSURE (aria-expanded + aria-controls,
   * a labelled group of buttons), not an ARIA menu — the account menu's pattern, and NK-126's
   * rule against borrowed widget semantics. Shift+F10 or the context-menu key/right-click on a
   * row opens the same panel (Param's "right-click"), in edit mode only, so the browser's
   * own link menu is untouched otherwise. Escape closes it and returns focus to its "…". */
  const [editing, setEditing] = React.useState(false);
  const [openMenu, setOpenMenu] = React.useState<string | null>(null);
  const [renaming, setRenaming] = React.useState<{ id: string; text: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = React.useState<string | null>(null);
  // Item 6: which section the "File a section" picker has chosen, per open panel.
  const [sectionPick, setSectionPick] = React.useState<Record<string, string>>({});
  const [editStatus, setEditStatus] = React.useState('');
  const menuTriggers = React.useRef<Map<string, HTMLButtonElement>>(new Map());
  const pencilRef = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (!editing) { setOpenMenu(null); setRenaming(null); setConfirmDelete(null); }
  }, [editing]);
  React.useEffect(() => {
    if (collapsed) setEditing(false);
  }, [collapsed]);

  const closeMenu = (key: string | null, refocus = true) => {
    setOpenMenu(null);
    setRenaming(null);
    setConfirmDelete(null);
    if (!refocus || !key) return;
    // After the re-render: a hidden or moved row's "…" is gone (or remounted elsewhere), so
    // focus goes to its trigger if it still exists, else to the pencil — never to <body>.
    window.setTimeout(() => {
      const trigger = menuTriggers.current.get(key);
      (trigger?.isConnected ? trigger : pencilRef.current)?.focus();
    }, 0);
  };
  const toggleMenu = (key: string) => {
    setRenaming(null);
    setConfirmDelete(null);
    setOpenMenu((cur) => (cur === key ? null : key));
  };
  /** Apply an op, announce it, and close the panel back to its trigger. */
  const act = (key: string, op: Parameters<typeof nav.apply>[0], message: string) => {
    nav.apply(op);
    setEditStatus(message);
    setDropNote('');
    closeMenu(key);
  };
  const groupName = (id: string) =>
    id === NEW_ID ? 'New' : (layoutGroups.find((g) => g.id === id)?.label ?? folderGroups.find((g) => g.id === id)?.label ?? 'the folder');

  /* ── SPEC-563 M2c — drag (D2: native HTML5 drag, no dependency) ──────────────────────
   * An extra way to do what the "…" menu does: desktop instance, edit mode only. A drop is
   * planned by the pure planDrop (config/nav-layout.ts) onto the SAME ops; a move the menu
   * does not offer is refused and announced, never ignored. The menu stays the accessible
   * path. Browser rules from the S1 spike: setData on dragstart (Firefox starts no drag
   * without data); never getData in dragover (empty in every browser); preventDefault on
   * dragover AND drop (an unhandled drop of "/path" text navigates in Firefox);
   * stopPropagation on each target so an outer handler cannot repaint the indicator. */
  const canDrag = editing && !readOnly;
  // The menu's own drag type. Dragover reads `types` (never the data) to accept ONLY a menu drag:
  // a stale dragRef (a source unmounted mid-drag, so no dragend) cannot adopt a text or file drag.
  const NAV_DRAG_TYPE = 'application/x-suryaiq-nav';
  const dragRef = React.useRef<{ item: DragItem; label: string } | null>(null);
  const [dragKey, setDragKey] = React.useState<string | null>(null);
  const [dropHint, setDropHint] = React.useState<{ key: string; zone: DropZone | 'into' } | null>(null);
  // The last refused drop, shown in the edit bar (the aria-live line already speaks it).
  const [dropNote, setDropNote] = React.useState('');
  /** Say a drop result on the aria-live line. The same sentence twice in a row gets a trailing
   *  no-break space, so a screen reader announces the repeat too. */
  const announceDrop = (words: string) => setEditStatus((cur) => (cur === words ? `${words}\u00a0` : words));
  const endDrag = () => {
    dragRef.current = null;
    setDragKey(null);
    setDropHint(null);
  };
  React.useEffect(() => {
    if (!canDrag) { endDrag(); setDropNote(''); }
  }, [canDrag]);

  const rowLabelIn = (groupId: string, entry: string) =>
    layoutGroups.find((g) => g.id === groupId)?.rows.find((r) => r.entry === entry)?.label ?? 'that screen';

  const refusalWords = (reason: DropRefusal, label: string, target: DropTarget): string => ({
    'no-change': `${label} is already there. Nothing moved.`,
    'favourites-by-star': 'Only the star adds a screen to Favourites. Nothing moved.',
    'not-to-new': 'Only a whole screen from a folder can go to New. Nothing moved.',
    'new-has-no-order': 'New keeps the standard order. Nothing moved.',
    'already-there': `${label} is already in ${groupName(target.group)}. Nothing moved.`,
    'folders-only': 'Drop a folder on the name of another folder. Nothing moved.',
    'folder-gone': 'That folder no longer exists. Nothing moved.',
    'not-movable': `${label} is no longer in your menu. Nothing moved.`,
    'anchor-gone': 'The screen you dropped it next to is no longer in your menu. Nothing moved.',
  })[reason];

  const movedWords = (item: DragItem, label: string, target: DropTarget): string => {
    const side = target.zone === 'before' ? 'above' : 'below';
    if (item.kind === 'folder') return `${label} moved ${side} ${groupName(target.group)}.`;
    if (target.group === NEW_ID) return `${label} moved to New.`;
    if (item.group === FAVOURITES_ID && target.group !== FAVOURITES_ID) {
      const holder = nav.layout.folders.find((f) => f.items.includes(item.entry));
      return `${label} ${holder ? `moved from ${holder.label} to` : 'filed in'} ${groupName(target.group)}. It stays in Favourites.`;
    }
    if (target.kind === 'heading') return `${label} moved to the end of ${groupName(target.group)}.`;
    const where = target.group === item.group ? '' : ` in ${groupName(target.group)}`;
    return `${label} moved ${side} ${rowLabelIn(target.group, target.entry)}${where}.`;
  };

  /** Props that make a row or folder heading a drag SOURCE (edit mode, desktop only). */
  const dragSource = (key: string, item: DragItem, label: string) => (canDrag ? {
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      e.stopPropagation();
      dragRef.current = { item, label };
      e.dataTransfer.effectAllowed = 'move';
      // text/plain: Firefox starts no drag without data (S1 spike step 2).
      e.dataTransfer.setData('text/plain', item.kind === 'entry' ? item.entry : label);
      e.dataTransfer.setData(NAV_DRAG_TYPE, key);
      // Fade the source after the browser has taken its drag image (review L9).
      window.setTimeout(() => { if (dragRef.current?.item === item) setDragKey(key); }, 0);
    },
    onDragEnd: endDrag,
  } : {});

  /** Props that make a row or heading a drop TARGET. `at` builds the target for a zone. */
  const dropTarget = (key: string, at: (zone: DropZone) => DropTarget) => {
    if (!canDrag) return {};
    const zoneOf = (e: React.DragEvent) => {
      const rect = e.currentTarget.getBoundingClientRect();
      return dropZone(e.clientY - rect.top, rect.height);
    };
    return {
      onDragOver: (e: React.DragEvent) => {
        const drag = dragRef.current;
        // Not a menu drag (a file, selected text, a link from elsewhere): the browser refuses it.
        if (!drag || !Array.from(e.dataTransfer.types).includes(NAV_DRAG_TYPE)) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
        const target = at(zoneOf(e));
        // A refused drop shows no indicator; it is still accepted so the refusal is announced.
        const zone = planDrop(nav.layout, drag.item, target).ok
          ? (drag.item.kind === 'entry' && target.kind === 'heading' ? 'into' : target.zone)
          : null;
        setDropHint((cur) => {
          if (!zone) return cur === null ? cur : null;
          return cur && cur.key === key && cur.zone === zone ? cur : { key, zone };
        });
      },
      onDragLeave: (e: React.DragEvent) => {
        // By position, not relatedTarget: Safari can report it as null, which would clear the
        // indicator on every crossing between the row's own link, star and "…" (review L2).
        const r = e.currentTarget.getBoundingClientRect();
        if (e.clientX > r.left && e.clientX < r.right && e.clientY > r.top && e.clientY < r.bottom) return;
        setDropHint((cur) => (cur && cur.key === key ? null : cur));
      },
      onDrop: (e: React.DragEvent) => {
        const drag = dragRef.current;
        if (!drag || !Array.from(e.dataTransfer.types).includes(NAV_DRAG_TYPE)) return;
        e.preventDefault();
        e.stopPropagation();
        const target = at(zoneOf(e));
        endDrag();
        // A row let go on itself is a cancelled drag, not a refused move: nothing to say.
        if (drag.item.kind === 'entry' && target.kind === 'row' && target.entry === drag.item.entry
          && target.group === drag.item.group) return;
        const plan = planDrop(nav.layout, drag.item, target);
        if (!plan.ok) {
          const words = refusalWords(plan.reason, drag.label, target);
          announceDrop(words);
          setDropNote(words);
          return;
        }
        nav.apply(plan.op);
        setDropNote('');
        announceDrop(movedWords(drag.item, drag.label, target));
      },
    };
  };

  /** The drop indicator: a 2px amber bar above or below the target, or a ring for "into".
   *  The bar is a pseudo-element ABOVE the row's children (z-10): an inset shadow on the line
   *  was painted over by the active row's opaque background (review M1). Neither shifts the
   *  rows under the pointer. */
  const HINT_BAR = "relative after:pointer-events-none after:absolute after:inset-x-0 after:h-0.5 after:rounded after:bg-amber-400 after:z-10 after:content-['']";
  const dropHintClass = (key: string) => {
    const zone = dropHint?.key === key ? dropHint.zone : null;
    return [
      dragKey === key ? 'opacity-50' : '',
      zone === 'before' ? `${HINT_BAR} after:top-0` : '',
      zone === 'after' ? `${HINT_BAR} after:bottom-0` : '',
      zone === 'into' ? 'ring-1 ring-amber-400 rounded-lg' : '',
    ].filter(Boolean).join(' ');
  };

  /** A row line in edit mode: its source + target props (spread on the line) and its class. */
  const rowDrag = (edit?: EditTarget): { props: object; cls: string } => {
    if (!edit || !canDrag) return { props: {}, cls: '' };
    const key = rowKeyOf(edit);
    const { group, row } = edit;
    return {
      props: {
        ...dragSource(key, { kind: 'entry', group: group.id, entry: row.entry }, row.label),
        ...dropTarget(key, (zone) => ({ kind: 'row', group: group.id, entry: row.entry, zone })),
      },
      cls: `cursor-grab ${dropHintClass(key)}`,
    };
  };

  /* ── SPEC-452 — page search ─────────────────────────────────────────────────
   * Type-ahead over the pages this user may actually see. Enter opens the top hit,
   * Escape clears. No new library: the matcher is config/nav-groups.ts. */
  const navigate = useNavigate();

  const [navQuery, setNavQuery] = React.useState('');
  const searchResults = React.useMemo(() => {
    // SPEC-563: a page the access-tag hook locks is out of search too (review finding 10).
    const pages = VIEW_REGISTRY.filter((p) => allowedPages.includes(p.path) && !nav.locked.includes(p.path));
    return searchPages(navQuery, pages)
      .map((p) => NAV_ITEMS.get(p.path))
      .filter((item): item is NavItem => Boolean(item));
  }, [navQuery, allowedPages.join('|'), nav.locked]);
  const searching = navQuery.trim().length > 0;
  // The search box is hidden while the sidebar is collapsed, so a query left behind must
  // not come back and silently filter the nav when the sidebar reopens.
  React.useEffect(() => {
    if (collapsed) setNavQuery('');
  }, [collapsed]);

  /* SPEC-462 — the rail's search button. It expands the sidebar first, so by the time this
   * runs with `collapsed` false the input is mounted. Token 0 is the initial value and never
   * a request, so a fresh mount does not steal focus. */
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  // Each token is honoured ONCE: without this, re-showing the menu after a search click
  // would steal focus into the box again (review finding 3).
  const handledSearchToken = React.useRef(0);
  React.useEffect(() => {
    if (!searchFocusToken || collapsed || handledSearchToken.current === searchFocusToken) return;
    handledSearchToken.current = searchFocusToken;
    searchInputRef.current?.focus({ preventScroll: true });
  }, [searchFocusToken, collapsed]);

  /* SPEC-462 — closes the "collapsed-nav focusability" backlog item. A collapsed panel
   * (the whole sidebar at w-0, or one nav group at grid-rows-[0fr]) is still in the DOM
   * with every row and star button in the tab order: a keyboard user tabbed through
   * dozens of invisible controls. `inert` removes them from focus AND the accessibility
   * tree without touching the collapse animation. Set imperatively because React 18's
   * types do not know the attribute. A LAYOUT effect on purpose: it must clear `inert`
   * before the search-focus effect above runs, or focusing the input on the rail's
   * search click fails silently (an inert subtree refuses focus). */
  const rootRef = React.useRef<HTMLDivElement>(null);
  React.useLayoutEffect(() => {
    rootRef.current?.toggleAttribute('inert', Boolean(collapsed));
  }, [collapsed]);

  // Announced to screen readers AND shown on screen, so "press Enter" has a referent in
  // both modalities (the top hit also carries a ring).
  const searchStatus = !searching
    ? ''
    : searchResults.length === 0
      ? `No page matches ${navQuery.trim()}`
      : `${searchResults.length === 1 ? '1 page' : `${searchResults.length} pages`} · Enter opens ${searchResults[0].name}`;

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setNavQuery('');
      return;
    }
    if (e.key === 'Enter' && searchResults.length > 0) {
      const top = searchResults[0];
      const keys = allowedSubKeys(top, workspace?.tabs?.[top.href], user?.tabs?.[top.href]);
      navigate(keys?.[0] ? `${top.href}?tab=${keys[0]}` : top.href);
      setNavQuery('');
      onNavigate?.();
    }
  };


  /** Is this page the one on screen? ONE definition, shared by the page rows, the
   *  pinned sub-view rows and the group headers — exact for '/', prefix otherwise, so a
   *  nested route like /products/:code still counts as being on /products. Two rows on
   *  the same screen disagreeing about where you are was SPEC-456 review finding 3. */
  const isPathActive = (href: string) =>
    (href === '/' ? location.pathname === '/' : location.pathname.startsWith(href));

  /* SPEC-535 — the "opens further" chevron. One entry per ROW (`${where}|${path}`), not per
   * page: a pinned page has a row in Favourites AND in its verb group, and each chevron
   * opens the list under its own row. A row with no entry follows the default (the row
   * that owns the page on screen shows its tabs). Cleared when the page changes, so the
   * menu settles back to "the page you are on is open" after every move. */
  const [tabsOpen, setTabsOpen] = React.useState<Record<string, boolean>>({});
  // Keeps the SAME object when there is nothing to clear, so a page change with no chevron
  // choices does not force an extra render (review finding 3).
  React.useEffect(() => {
    setTabsOpen((prev) => (Object.keys(prev).length ? {} : prev));
  }, [location.pathname]);

  // SPEC-505 — follow the active page. The target is the group that holds the page's own row
  // (never Favourites; SPEC-563: a folder or New); a standalone page may have none.
  const navScrollRef = React.useRef<HTMLElement>(null);
  // SPEC-563 fix loop 2 (item 6 review L3): a filed section row that is the section on screen
  // wins over the page's own row, so clicking it does not pull the menu to another folder.
  const followGroup = followTarget(
    layoutGroups.map((g) => ({ id: g.id, items: g.visible })),
    isPathActive,
    activeSubKey,
  );
  useFollowActiveNav({
    containerRef: navScrollRef,
    navKey: `${location.pathname}${location.search}`,
    collapsed: Boolean(collapsed),
    activeGroup: followGroup,
    activeGroupOpen: followGroup ? groupState[followGroup] !== false : true,
    openGroup,
  });

  /** One nav row. Exactly ONE row per page owns the sub-tab strip (SPEC-452): the
   *  Favourites row when the page is pinned, otherwise its verb-group row. Search
   *  results never own it. Callers pass `ownsSub` explicitly — never via `.map`,
   *  which would feed the array INDEX in as this argument.
   *  SPEC-535: "owning" now means "opens by itself while you are on the page". Every row
   *  of a page with tabs carries the chevron, and a click on it opens THAT row's list
   *  (`where` names the row: a group label, Favourites, or 'search'). */
  function renderNavItem(item: NavItem, where: string, ownsSub: boolean, edit?: EditTarget) {
    // SPEC-288: the Demand Planning entry carries the open demand-request count (planner
    // tier only); every other entry keeps its existing alert-count source.
    const count = item.href === '/demand-planning'
      ? demandRequestPending
      : (item.alertKey ? alertCounts[item.alertKey] : 0);
    const isFuture = item.status === 'future';
    const isPageActive = isPathActive(item.href);
    const visibleKeys = allowedSubKeys(item, workspace?.tabs?.[item.href], user?.tabs?.[item.href]);
    const firstKey = visibleKeys?.[0];
    const isFavourite = favourites.includes(item.href);
    const hasTabs = opensFurther(visibleKeys) && Boolean(item.subGroups);
    const rowKey = `${where}|${item.href}`;
    const expanded = tabsExpanded(hasTabs, tabsOpen[rowKey], ownsSub && isPageActive);
    // A DOM id for aria-controls. Built from the row key (a render function, not a component,
    // so no useId here); one row per key by construction within ONE sidebar. The instance
    // prefix matters on a phone: the desktop sidebar stays mounted (display:none) under the
    // overlay menu, so without it aria-controls would resolve to the hidden copy.
    const tabsId = `nav-tabs-${onNavigate ? 'menu' : 'side'}-${rowKey.replace(/[^A-Za-z0-9]+/g, '-')}`;
    // SPEC-563 M2c: in edit mode the row LINE (not the open panel or tab strip under it) is
    // the drag source and drop target, so its height is the hit zone.
    const drag = rowDrag(edit);
    return (
      // Keyed on the PATH, not the label: a group can mix page rows with sub-view rows
      // (keyed on their stored entry), and every entry starts with '/', so one key space
      // makes collision-freedom structural.
      <div key={item.href} {...rowMenuHandlers(edit)}>
        {/* The row is a flex pair, not one anchor: an interactive star nested inside the
            NavLink would be a nested-interactive a11y violation and would swallow the
            navigation click. `group/nav` scopes the hover reveal to THIS row. */}
        <div className={`flex items-center gap-0.5 group/nav ${drag.cls}`} {...drag.props}>
          <NavLink
            to={firstKey ? `${item.href}?tab=${firstKey}` : item.href}
            end={item.href === '/'}
            // A link drags itself by default (its URL); in edit mode the row line drags instead.
            draggable={canDrag && edit ? false : undefined}
            // Clearing the query on any nav click restores the groups (and the active
            // page's tab strip) — a click means the planner found what they searched for.
            // SPEC-535: the name click also drops this row's chevron choice, so the page
            // you open shows its tabs (navigates AND expands).
            onClick={() => {
              setNavQuery('');
              setTabsOpen((prev) => {
                if (!(rowKey in prev)) return prev;
                const { [rowKey]: _dropped, ...rest } = prev;
                return rest;
              });
              onNavigate?.();
            }}
            // SPEC-371: `opacity-60` used to mark a future-scope item. It composited
            // --text-secondary down to #595a61 on --sidebar-bg — 2.82:1, well under AA.
            // Replaced with explicit colours, one per state.
            //
            // All FOUR combinations are enumerated deliberately. The old code appended the
            // dimming OUTSIDE the isActive ternary, so a future page still read as future
            // while you were on it; folding isFuture into the inactive branch alone would
            // make an active future page pixel-identical to an active live one, and all
            // five future pages are navigable (/reports, /supplier-risk, /retro-billing,
            // /admin, /super-admin). Active+future therefore keeps the amber treatment but
            // one step down (amber-600: 5.38:1 on the active background, and 1.91:1 apart
            // from amber-400 — the widest separation of the amber steps that still clears AA).
            //
            // Ceiling worth knowing: a normal item is 5.88:1 and the AA floor is 4.5:1, so
            // every compliant "dim" lands within ~1.3:1 of the normal colour. Status here is
            // therefore conveyed by colour alone — the status dot is NOT a usable backstop
            // (pinned at opacity-30 it composites to #25252a, ~1.27:1, effectively
            // invisible) and its `title` is unavailable to touch and keyboard users. A
            // non-colour cue is the honest fix; SPEC-371's brief reports it.
            //
            // One text- utility per branch on purpose: two arbitrary-value text utilities
            // tie on specificity and resolve by stylesheet order, not class order.
            // SPEC-535: size, weight, height and indent come from NAV_SCALE.page — one tier.
            className={({ isActive }) =>
              `flex flex-1 min-w-0 items-center ${NAV_SCALE.page.row} ${NAV_SCALE.page.text} rounded-lg transition-colors relative ${
                isActive
                  ? isFuture
                    ? `bg-[var(--sidebar-active-bg)] text-amber-600 border-l-2 border-amber-600 ${NAV_SCALE.page.indentActive}`
                    : `bg-[var(--sidebar-active-bg)] text-amber-400 border-l-2 border-amber-400 ${NAV_SCALE.page.indentActive}`
                  : isFuture
                    ? `${NAV_SCALE.page.indent} text-[var(--text-tertiary)] hover:bg-[var(--sidebar-hover)] hover:text-[var(--text-primary)]`
                    : `${NAV_SCALE.page.indent} text-[var(--text-secondary)] hover:bg-[var(--sidebar-hover)] hover:text-[var(--text-primary)]`
              }`
            }
          >
            <span className="flex-1">{item.name}</span>
            {/* SPEC-527 fix loop 4: a count whose read failed is not known — a neutral "?",
                never a hidden badge (which reads as "nothing to act on"). */}
            {count === null && (
              <span
                className="px-1.5 py-0.5 text-[10px] font-bold border border-[var(--text-tertiary)] text-[var(--text-secondary)] rounded-full min-w-[18px] text-center"
                title="Count not known: the alerts could not be read just now. It is not zero."
                aria-label="alert count not known"
              >
                {alertBadgeText(count)}
              </span>
            )}
            {count !== null && count > 0 && (
              <span
                className="px-1.5 py-0.5 text-[10px] font-bold bg-amber-500 text-[var(--text-inverse)] rounded-full min-w-[18px] text-center"
                title={item.href === '/demand-planning'
                  ? `${count} demand request${count === 1 ? '' : 's'} waiting on a decision`
                  : undefined}
              >
                {count > 99 ? '99+' : count}
              </span>
            )}
            <span
              className={`w-2 h-2 rounded-full flex-shrink-0 ${
                item.status === 'active'
                  ? 'bg-green-400'
                  : item.status === 'development'
                    ? 'bg-amber-400'
                    // SPEC-371: was opacity-50, which the anchor's now-removed opacity-60
                    // multiplied to 0.30 effective. Pinned at 30 so the dot renders exactly
                    // as it did pre-367 (#25252a on --sidebar-bg) rather than brightening.
                    : 'bg-[var(--fill-muted)] opacity-30'
              }`}
              title={
                item.status === 'active'
                  ? 'Live'
                  : item.status === 'development'
                    ? 'In development'
                    : 'Future scope'
              }
            />
          </NavLink>
          {/* SPEC-535 — "opens further". Shown on EVERY row of a page that has tabs, always
              visible (never hover-only: a tablet has no hover, and an affordance you must
              hover to find is not an affordance). It is a sibling of the link, not inside it,
              for the same reason as the star: a button nested in a link is a nested-
              interactive fault and would swallow the navigation. A click toggles the list
              WITHOUT navigating; the name navigates. Right-pointing when closed, down when
              open. A page without tabs keeps the slot EMPTY, so the dots and stars stay in
              one column down the menu. The label is stable and aria-expanded carries the
              state (the star's contract). */}
          {hasTabs ? (
            <button
              type="button"
              onClick={() => setTabsOpen((prev) => ({ ...prev, [rowKey]: !expanded }))}
              aria-expanded={expanded}
              aria-controls={expanded ? tabsId : undefined}
              aria-label={`Tabs in ${item.name}`}
              title={expanded ? 'Hide the tabs' : 'Show the tabs'}
              className="flex-shrink-0 p-1.5 rounded text-[var(--text-secondary)] transition-colors hover:bg-[var(--sidebar-hover)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-500/60"
            >
              <ChevronRight
                className={`${NAV_SCALE.page.icon} transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
                aria-hidden
              />
            </button>
          ) : (
            <span className="flex-shrink-0 w-[26px]" aria-hidden />
          )}
          {/* SPEC-452 — pin control. Always in the tab order, and `focus-visible:opacity-100`
              makes it visible to keyboard users; `opacity-0` only hides the resting state of
              an unpinned icon, so it carries no text and no contrast risk.
              `[@media(hover:none)]:opacity-100` keeps the star usable on a tablet, where
              there is no hover to reveal it at all (Harry/Jaggi/Ross are not always desktop).
              The label stays STABLE and `aria-pressed` carries the state — a label that also
              flipped would make a screen reader announce the toggle twice. */}
          <button
            type="button"
            onClick={() => toggleFavourite(item.href)}
            aria-pressed={isFavourite}
            aria-label={`Pin ${item.name} to Favourites`}
            title={isFavourite ? 'Unpin from Favourites' : 'Pin to Favourites'}
            // SPEC-456 review finding 4: p-1.5 + w-3.5 = a 28px target, clearing the
            // WCAG 2.5.8 24px floor. The star is revealed on touch and sits beside a
            // navigation link, so an under-size target mis-navigates on a tablet.
            className={`flex-shrink-0 p-1.5 rounded transition-colors hover:bg-[var(--sidebar-hover)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-500/60 ${
              isFavourite
                ? 'text-amber-400'
                : 'text-[var(--text-tertiary)] opacity-0 group-hover/nav:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100 hover:text-amber-400'
            }`}
          >
            <Star className={NAV_SCALE.page.icon} fill={isFavourite ? 'currentColor' : 'none'} />
          </button>
          {edit && renderRowMenuTrigger(edit)}
          </div>
        {edit && renderRowMenuPanel(edit)}
        {expanded && item.subGroups && (
          <SectionNav
            id={tabsId}
            // A list opened by the chevron on a page you are NOT on marks no tab as current.
            pageActive={isPageActive}
            groups={item.subGroups}
            basePath={item.href}
            activeKey={activeSubKey}
            visibleKeys={visibleKeys}
            onNavigate={onNavigate}
            // SPEC-456 — the same star, on the sub-view rows. The strip renders only for
            // the page you are ON, so a sub-view is pinned from the page it belongs to.
            pinnedTabKeys={pinnedTabsFor(item.href)}
            onTogglePin={(tabKey) => toggleFavourite(`${item.href}?tab=${tabKey}`)}
            pageLabel={item.name}
          />
        )}
      </div>
    );
  }

  /** One SUB-VIEW row (SPEC-456; SPEC-563: in Favourites or in any folder). The same single-line
   *  grammar as a page row — including the page's status dot, so the right-hand column
   *  still lines up and the dot keeps meaning what it means (a sub-view carries its
   *  page's status). The alert BADGE is deliberately absent: that count belongs to the
   *  page, and beside a sub-view's name it would read as the sub-view's own. The row
   *  never owns a sub-tab strip; the page's own row does. */
  function renderSubViewRow(fav: ResolvedEntry, edit?: EditTarget) {
    const isFavourite = favourites.includes(fav.entry);
    const target = `${fav.item.href}?tab=${fav.tabKey}`;
    const isFuture = fav.item.status === 'future';
    // Matched MANUALLY, not by NavLink: NavLink compares the pathname only, so every
    // pinned sub-view of the open page would read as active. Mirrors SectionNav's rule,
    // including a URL with no ?tab= landing on the first visible sub-view.
    const visibleKeys = allowedSubKeys(
      fav.item,
      workspace?.tabs?.[fav.item.href],
      user?.tabs?.[fav.item.href],
    );
    const isActive = isPathActive(fav.item.href)
      && (activeSubKey === fav.tabKey || (!activeSubKey && visibleKeys?.[0] === fav.tabKey));
    const drag = rowDrag(edit);
    return (
      <div key={fav.entry} {...rowMenuHandlers(edit)}>
      <div className={`flex items-center gap-0.5 group/nav ${drag.cls}`} {...drag.props}>
        <NavLink
          to={target}
          draggable={canDrag && edit ? false : undefined}
          onClick={() => { setNavQuery(''); onNavigate?.(); }}
          aria-current={isActive ? 'page' : undefined}
          // The same four enumerated colour states as the page row (SPEC-371): no
          // `opacity-*` on text anywhere, and an active future-scope page keeps the
          // amber treatment one step down rather than reading as live.
          // SPEC-535: a pinned sub-view sits at PAGE level in Favourites, so it takes the page
          // tier of NAV_SCALE — the same size, height and indent as the page rows around it.
          className={`flex flex-1 min-w-0 items-center ${NAV_SCALE.page.row} ${NAV_SCALE.page.text} rounded-lg transition-colors relative ${
            isActive
              ? isFuture
                ? `bg-[var(--sidebar-active-bg)] text-amber-600 border-l-2 border-amber-600 ${NAV_SCALE.page.indentActive}`
                : `bg-[var(--sidebar-active-bg)] text-amber-400 border-l-2 border-amber-400 ${NAV_SCALE.page.indentActive}`
              : isFuture
                ? `${NAV_SCALE.page.indent} text-[var(--text-tertiary)] hover:bg-[var(--sidebar-hover)] hover:text-[var(--text-primary)]`
                : `${NAV_SCALE.page.indent} text-[var(--text-secondary)] hover:bg-[var(--sidebar-hover)] hover:text-[var(--text-primary)]`
          }`}
        >
          <span className="flex-1">{fav.label}</span>
          <span
            className={`w-2 h-2 rounded-full flex-shrink-0 ${
              fav.item.status === 'active'
                ? 'bg-green-400'
                : fav.item.status === 'development'
                  ? 'bg-amber-400'
                  : 'bg-[var(--fill-muted)] opacity-30'
            }`}
            title={
              fav.item.status === 'active'
                ? 'Live'
                : fav.item.status === 'development'
                  ? 'In development'
                  : 'Future scope'
            }
          />
        </NavLink>
        {/* SPEC-535: a sub-view opens no further, so the chevron slot stays empty — the
            dots and stars stay in the same column as the page rows. */}
        <span className="flex-shrink-0 w-[26px]" aria-hidden />
        {/* Lit in Favourites (where the row exists only while the pin does); in a folder it
            shows whether the sub-view is also starred. Same stable label + aria-pressed
            contract as the page row's control. */}
        <button
          type="button"
          onClick={() => toggleFavourite(fav.entry)}
          aria-pressed={isFavourite}
          aria-label={`Pin ${fav.label} to Favourites`}
          title={isFavourite ? 'Unpin from Favourites' : 'Pin to Favourites'}
          className={`flex-shrink-0 p-1.5 rounded transition-colors hover:bg-[var(--sidebar-hover)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-500/60 ${
            isFavourite
              ? 'text-amber-400'
              : 'text-[var(--text-tertiary)] opacity-0 group-hover/nav:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100 hover:text-amber-400'
          }`}
        >
          <Star className={NAV_SCALE.page.icon} fill={isFavourite ? 'currentColor' : 'none'} />
        </button>
        {edit && renderRowMenuTrigger(edit)}
      </div>
      {edit && renderRowMenuPanel(edit)}
      </div>
    );
  }

  /* ── SPEC-563 — the "…" disclosures (edit mode) ──────────────────────────────────
   * One panel per row or folder, opened by its "…" button, Shift+F10, or the context-menu
   * key / right-click on the row. Every operation the layout model has is reachable here by
   * keyboard alone; drag (if present) is an extra way to do the same ops. */
  const panelId = (key: string) => `nav-edit-${onNavigate ? 'menu' : 'side'}-${key.replace(/[^A-Za-z0-9]+/g, '-')}`;
  const rowKeyOf = (edit: EditTarget) => `row|${edit.group.id}|${edit.row.entry}`;

  // Focus lands on the panel's first control ONCE: when it opens, or when it switches to
  // Rename or Delete. Never on a later re-render (an alert poll, a save answer), which would
  // pull a keyboard user back into the panel after they tabbed away (review finding 2).
  React.useEffect(() => {
    if (!openMenu) return;
    document.getElementById(panelId(openMenu))
      ?.querySelector<HTMLElement>('button:not([disabled]), input, select')
      ?.focus();
    // panelId is a pure function of the key; it is not a dependency.
  }, [openMenu, renaming?.id, confirmDelete]);

  function rowMenuHandlers(edit?: EditTarget) {
    if (!edit) return {};
    const key = rowKeyOf(edit);
    return {
      onContextMenu: (e: React.MouseEvent) => { e.preventDefault(); toggleMenu(key); },
      onKeyDown: (e: React.KeyboardEvent) => {
        if ((e.shiftKey && e.key === 'F10') || e.key === 'ContextMenu') {
          e.preventDefault();
          setOpenMenu(key);
        }
      },
    };
  }

  function renderMenuTrigger(key: string, name: string) {
    const open = openMenu === key;
    return (
      <button
        type="button"
        ref={(el) => { if (el) menuTriggers.current.set(key, el); else menuTriggers.current.delete(key); }}
        onClick={() => toggleMenu(key)}
        aria-expanded={open}
        aria-controls={open ? panelId(key) : undefined}
        aria-label={`Arrange ${name}`}
        title="Move, hide or file this"
        className="flex-shrink-0 p-1.5 rounded text-[var(--text-secondary)] transition-colors hover:bg-[var(--sidebar-hover)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-500/60"
      >
        <MoreHorizontal className={NAV_SCALE.page.icon} aria-hidden />
      </button>
    );
  }

  const PANEL_BTN = 'w-full text-left px-2 py-1.5 rounded text-xs text-[var(--text-secondary)] hover:bg-[var(--sidebar-hover)] hover:text-[var(--text-primary)] disabled:text-[var(--text-tertiary)] disabled:hover:bg-transparent disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-500/60';

  function renderPanel(key: string, label: string, body: React.ReactNode) {
    if (openMenu !== key) return null;
    return (
      <div
        id={panelId(key)}
        role="group"
        aria-label={label}
        onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); closeMenu(key); } }}
        className="ml-3 my-1 p-1.5 rounded-lg border border-[var(--border-default)] bg-[var(--bg-raised)] space-y-0.5"
      >
        {body}
      </div>
    );
  }

  function renderRowMenuTrigger(edit: EditTarget) {
    return renderMenuTrigger(rowKeyOf(edit), edit.row.label);
  }

  function renderRowMenuPanel(edit: EditTarget) {
    const key = rowKeyOf(edit);
    const { group, row } = edit;
    const at = group.visible.indexOf(row.entry);
    const last = group.visible.length - 1;
    const ordered = group.id !== NEW_ID;
    const nudge = (to: Nudge, words: string) =>
      act(key, (l) => nudgeEntry(l, group.id, row.entry, group.visible, to), `${row.label} ${words}.`);
    // SPEC-563 fix loop 2 (Codex finding 4): a Favourites row can be FILED into a folder (it
    // stays starred). A starred sub-view has no other row, so this is its only way into a folder.
    const fromFavourites = group.id === FAVOURITES_ID;
    const destinations = moveDestinations(nav.layout, group.id, row.entry, folderGroups);
    const selectId = `${panelId(key)}-dest`;
    /* SPEC-563 fix loop 2, item 6 (Param, 15 Oct: "move the nested pages… into a new folder"):
     * a PAGE row offers its own sections (the sub-views this login may see) to file into a
     * folder as rows of their own. Same mechanism as Favourites "File in" (moveDestinations,
     * filing source SECTIONS_ID): the folder gains a row that opens the page on that section;
     * the page keeps its own row and its section list. The registry is the only source of
     * which sections exist (via the page's subGroups), narrowed by the same workspace/user
     * filtering as the section list itself. */
    const sectionKeys = row.tabKey ? undefined : allowedSubKeys(row.item, workspace?.tabs?.[row.item.href], user?.tabs?.[row.item.href]);
    const sections = offeredSections(row.item.href, row.item.subGroups, sectionKeys);
    const folderOf = (entry: string) => nav.layout.folders.find((f) => f.items.includes(entry));
    /** Review L5: a folder deleted in another window between opening and submitting. */
    const folderGone = (to: string) => !nav.layout.folders.some((f) => f.id === to);
    const pickedSection = sections.find((sct) => sct.entry === sectionPick[key]) ?? sections[0];
    const sectionDest = pickedSection ? moveDestinations(nav.layout, SECTIONS_ID, pickedSection.entry, folderGroups) : [];
    const sectionId = `${panelId(key)}-section`;
    const sectionFolderId = `${panelId(key)}-section-folder`;
    // A filed SECTION row in a folder can leave that folder again (it is never "New").
    const inFolder = group.id !== FAVOURITES_ID && group.id !== NEW_ID;
    return renderPanel(key, `Arrange ${row.label}`, (
      <>
        {ordered && (
          <>
            <button type="button" className={PANEL_BTN} disabled={at <= 0} onClick={() => nudge('up', 'moved up')}>Move up</button>
            <button type="button" className={PANEL_BTN} disabled={at < 0 || at >= last} onClick={() => nudge('down', 'moved down')}>Move down</button>
            <button type="button" className={PANEL_BTN} disabled={at <= 0} onClick={() => nudge('top', 'moved to the top')}>Move to top</button>
            <button type="button" className={PANEL_BTN} disabled={at < 0 || at >= last} onClick={() => nudge('bottom', 'moved to the bottom')}>Move to bottom</button>
          </>
        )}
        {destinations.length > 0 && (
          <form
            className="flex items-center gap-1 px-1 py-1"
            onSubmit={(e) => {
              e.preventDefault();
              const to = (e.currentTarget.elements.namedItem('dest') as HTMLSelectElement).value;
              if (to !== NEW_ID && folderGone(to)) {
                setEditStatus('That folder no longer exists. Nothing was moved.');
                closeMenu(key);
                return;
              }
              // A starred entry already filed elsewhere is MOVED (one folder per entry); say so.
              const holder = folderOf(row.entry);
              if (fromFavourites) {
                act(key, (l) => fileEntry(l, row.entry, to),
                  `${row.label} ${holder ? `moved from ${holder.label} to` : 'filed in'} ${groupName(to)}. It stays in Favourites.`);
              } else {
                act(key, (l) => moveEntry(l, row.entry, to, to === NEW_ID ? 0 : Number.MAX_SAFE_INTEGER),
                  `${row.label} moved to ${to === NEW_ID ? 'New' : groupName(to)}.`);
              }
            }}
          >
            <label htmlFor={selectId} className="text-xs text-[var(--text-secondary)] whitespace-nowrap">{fromFavourites ? 'File in' : 'Move to'}</label>
            <select id={selectId} name="dest" className="min-w-0 flex-1 text-xs bg-[var(--bg-base)] border border-[var(--border-default)] rounded px-1 py-1 text-[var(--text-primary)]">
              {destinations.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
            </select>
            <button type="submit" className="px-2 py-1 rounded text-xs bg-[var(--bg-overlay)] text-[var(--text-primary)] hover:bg-[var(--sidebar-hover)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-500/60">{fromFavourites ? 'File' : 'Move'}</button>
          </form>
        )}
        {sections.length > 0 && pickedSection && (
          <form
            className="space-y-1 px-1 py-1"
            onSubmit={(e) => {
              e.preventDefault();
              const to = (e.currentTarget.elements.namedItem('sectionDest') as HTMLSelectElement | null)?.value;
              if (!to) return;
              if (folderGone(to)) {
                setEditStatus('That folder no longer exists. Nothing was filed.');
                closeMenu(key);
                return;
              }
              const label = `${row.item.name} · ${pickedSection.label}`;
              // One folder per entry: filing a section that is already in a folder MOVES it; say so.
              const holder = folderOf(pickedSection.entry);
              act(key, (l) => fileEntry(l, pickedSection.entry, to),
                `${label} ${holder ? `moved from ${holder.label} to` : 'filed in'} ${groupName(to)}. ${row.item.name} keeps its own row.`);
            }}
          >
            <div className="flex items-center gap-1">
              <label htmlFor={sectionId} className="text-xs text-[var(--text-secondary)] whitespace-nowrap">File a section</label>
              <select
                id={sectionId}
                value={pickedSection.entry}
                onChange={(e) => setSectionPick((prev) => ({ ...prev, [key]: e.target.value }))}
                className="min-w-0 flex-1 text-xs bg-[var(--bg-base)] border border-[var(--border-default)] rounded px-1 py-1 text-[var(--text-primary)]"
              >
                {sections.map((sct) => {
                  const at = folderOf(sct.entry);
                  return <option key={sct.entry} value={sct.entry}>{at ? `${sct.label} (in ${at.label})` : sct.label}</option>;
                })}
              </select>
            </div>
            {sectionDest.length > 0 ? (
              <div className="flex items-center gap-1">
                <label htmlFor={sectionFolderId} className="text-xs text-[var(--text-secondary)] whitespace-nowrap">In folder</label>
                <select id={sectionFolderId} name="sectionDest" className="min-w-0 flex-1 text-xs bg-[var(--bg-base)] border border-[var(--border-default)] rounded px-1 py-1 text-[var(--text-primary)]">
                  {sectionDest.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
                </select>
                <button type="submit" className="px-2 py-1 rounded text-xs bg-[var(--bg-overlay)] text-[var(--text-primary)] hover:bg-[var(--sidebar-hover)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-500/60">File section</button>
              </div>
            ) : (
              <p className="text-[11px] text-[var(--text-tertiary)]">
                {folderGroups.length === 0 ? 'Make a folder first.' : 'This section is already in your only folder.'}
              </p>
            )}
          </form>
        )}
        {row.tabKey && inFolder && (
          <button type="button" className={PANEL_BTN} onClick={() => act(key, (l) => moveEntry(l, row.entry, NEW_ID, 0), `${row.label} removed from ${group.label}. It is still a section of ${row.item.name}.`)}>Remove from folder</button>
        )}
        {fromFavourites ? (
          <button type="button" className={PANEL_BTN} onClick={() => act(key, (l) => setFavourite(l, row.entry, false), `${row.label} removed from Favourites.`)}>Remove from Favourites</button>
        ) : (
          <button type="button" className={PANEL_BTN} onClick={() => act(key, (l) => hide(l, row.entry), `${row.label} hidden. Show it again from Arrange menu.`)}>Hide</button>
        )}
      </>
    ));
  }

  function renderFolderMenuPanel(group: ResolvedGroup) {
    const key = `folder|${group.id}`;
    if (group.id === NEW_ID) {
      const selectId = `${panelId(key)}-dest`;
      return renderPanel(key, 'Arrange New', folderGroups.length ? (
        <form
          className="flex items-center gap-1 px-1 py-1"
          onSubmit={(e) => {
            e.preventDefault();
            const to = (e.currentTarget.elements.namedItem('dest') as HTMLSelectElement).value;
            act(key, (l) => fileNewPages(l, group.visible, to), `New screens filed into ${groupName(to)}.`);
          }}
        >
          <label htmlFor={selectId} className="text-xs text-[var(--text-secondary)] whitespace-nowrap">File all into</label>
          <select id={selectId} name="dest" className="min-w-0 flex-1 text-xs bg-[var(--bg-base)] border border-[var(--border-default)] rounded px-1 py-1 text-[var(--text-primary)]">
            {folderGroups.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
          </select>
          <button type="submit" className="px-2 py-1 rounded text-xs bg-[var(--bg-overlay)] text-[var(--text-primary)] hover:bg-[var(--sidebar-hover)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-500/60">File</button>
        </form>
      ) : <p className="px-2 py-1 text-xs text-[var(--text-tertiary)]">Make a folder first, then file these into it.</p>);
    }
    // Position among the STORED folders (a folder this login cannot see still holds a place).
    const order = nav.layout.folders.map((f) => f.id);
    const shown = folderGroups.map((g) => g.id);
    const at = shown.indexOf(group.id);
    const moveTo = (to: number, words: string) =>
      act(key, (l) => moveFolder(l, group.id, order.indexOf(shown[to])), `${group.label} ${words}.`);
    const renameId = `${panelId(key)}-name`;
    return renderPanel(key, `Arrange folder ${group.label}`, renaming?.id === group.id ? (
      <form
        className="space-y-1 px-1 py-1"
        onSubmit={(e) => {
          e.preventDefault();
          const clean = cleanLabel(renaming.text);
          if (clean === null) return;
          act(key, (l) => renameFolder(l, group.id, clean), `Folder renamed to ${clean}.`);
        }}
      >
        <label htmlFor={renameId} className="block text-xs text-[var(--text-secondary)]">Folder name</label>
        <input
          id={renameId}
          value={renaming.text}
          maxLength={40}
          onChange={(e) => setRenaming({ id: group.id, text: e.target.value })}
          className="w-full text-xs bg-[var(--bg-base)] border border-[var(--border-default)] rounded px-2 py-1 text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-amber-500/40"
        />
        <div className="flex justify-end gap-1">
          <button type="button" className="px-2 py-1 rounded text-xs text-[var(--text-secondary)] hover:bg-[var(--sidebar-hover)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-500/60" onClick={() => setRenaming(null)}>Cancel</button>
          <button type="submit" disabled={cleanLabel(renaming.text) === null} className="px-2 py-1 rounded text-xs bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-500/60">Save</button>
        </div>
      </form>
    ) : confirmDelete === group.id ? (
      <div className="space-y-1 px-1 py-1">
        <p className="text-xs text-[var(--text-secondary)]">Delete {group.label}? Its screens move to New. Nothing is hidden.</p>
        <div className="flex justify-end gap-1">
          <button type="button" className="px-2 py-1 rounded text-xs text-[var(--text-secondary)] hover:bg-[var(--sidebar-hover)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-500/60" onClick={() => setConfirmDelete(null)}>Cancel</button>
          <button type="button" className="px-2 py-1 rounded text-xs bg-red-500/90 text-white hover:bg-red-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-500/60" onClick={() => act(key, (l) => deleteFolder(l, group.id), `${group.label} deleted. Its screens are in New.`)}>Delete folder</button>
        </div>
      </div>
    ) : (
      <>
        <button type="button" className={PANEL_BTN} disabled={at <= 0} onClick={() => moveTo(at - 1, 'moved up')}>Move folder up</button>
        <button type="button" className={PANEL_BTN} disabled={at < 0 || at >= shown.length - 1} onClick={() => moveTo(at + 1, 'moved down')}>Move folder down</button>
        <button type="button" className={PANEL_BTN} onClick={() => setRenaming({ id: group.id, text: group.label })}>Rename</button>
        <button type="button" className={PANEL_BTN} onClick={() => setConfirmDelete(group.id)}>Delete folder</button>
      </>
    ));
  }

  /** The collapsible group shell, keyed by group id (SPEC-563: Favourites, New and every
   *  folder share it, so a folder behaves exactly like the verb groups did). */
  function renderGroupShell(group: ResolvedGroup, hasActiveChild: boolean, rows: React.ReactNode) {
    const isOpen = groupState[group.id] !== false;
    const mark = markFor(group.id, group.label);
    const folderKey = `folder|${group.id}`;
    const editable = editing && group.id !== FAVOURITES_ID;
    // SPEC-563 M2c: every heading is a drop target (a row lands "into" the folder; a folder
    // lands above or below); only a stored folder's heading drags (not Favourites, not New).
    const isFolder = group.id !== FAVOURITES_ID && group.id !== NEW_ID;
    const headingTarget = dropTarget(folderKey, (zone) => ({ kind: 'heading', group: group.id, zone }));
    // The empty-folder line sits INSIDE the folder: a folder dropped there goes below this one
    // (its own halves would not match the bar drawn on the heading — review L10).
    const emptyTarget = dropTarget(folderKey, () => ({ kind: 'heading', group: group.id, zone: 'after' }));
    return (
      <div key={group.id} className="mt-3">
        <div
          className={`flex items-center gap-0.5 ${canDrag ? `${isFolder ? 'cursor-grab ' : ''}${dropHintClass(folderKey)}` : ''}`}
          {...(isFolder ? dragSource(folderKey, { kind: 'folder', folder: group.id }, group.label) : {})}
          {...headingTarget}
        >
        <button
          onClick={() => toggleGroup(group.id)}
          aria-expanded={isOpen}
          data-nav-group={group.id}
          className={`flex-1 min-w-0 flex items-center gap-2 ${NAV_SCALE.group.row} group rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/60`}
        >
          {/* SPEC-509 — the group's B mark, on the HEADING only (never on a page row:
              SPEC-456 holds). Lines are secondary ink; the node takes the heading colour at
              rest and turns amber-400 when this group holds the page on screen — that node
              replaced the old 6px "active child" dot, and it shows open or closed.
              SPEC-563: a folder the user made or renamed, and New, carry no verb mark (an
              empty slot of the same size keeps the headings in one column). */}
          <span
            className={`shrink-0 grid place-items-center ${
              hasActiveChild
                ? 'text-[var(--text-primary)] [--mark-accent:var(--amber-400)]'
                : 'text-[var(--text-secondary)] [--mark-accent:var(--nav-heading)]'
            }`}
            style={mark ? undefined : { width: NAV_SCALE.group.mark, height: NAV_SCALE.group.mark }}
          >
            {mark && <GroupMark id={mark} size={NAV_SCALE.group.mark} />}
          </span>
          {/* SPEC-459 — the ONE heading treatment. `--nav-heading` (SPEC-509: yellow-500) is
              the "this is a grouping, not a destination" signal, and it is shared verbatim
              with SectionNav's group labels so the sidebar and the sub-nav strip read as
              one grammar. Hover lifts within the same hue rather than to --text-primary:
              this header is also the group's collapse control, so it must answer the
              pointer without borrowing the active-location amber-400.
              Colour is not the only cue. SPEC-535 (NK-176): the heading is now the LARGEST
              tier of NAV_SCALE (14px semibold, normal case, a 20px mark) and the rows step
              down from it (13px medium, indented one step, 12px sub-tabs two steps). Heading
              vs active row is still told apart by the mark, the indent, the weight and the
              row's tint and left bar — never by colour alone. */}
          <span className={`${NAV_SCALE.group.text} text-[var(--nav-heading)] group-hover:text-[var(--nav-heading-hover)] transition-colors flex-1 text-left truncate`}>
            {group.label}
            {/* The amber node is colour; this says the same in words. */}
            {hasActiveChild && <span className="sr-only">, holds the page you are on</span>}
          </span>
          <ChevronDown className={`${NAV_SCALE.group.chevron} text-[var(--text-tertiary)] transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`} />
        </button>
        {editable && renderMenuTrigger(folderKey, group.id === NEW_ID ? 'New' : `folder ${group.label}`)}
        </div>
        {editable && renderFolderMenuPanel(group)}
        {/* grid-rows collapse: height is content-driven, so grouped sub-navs
            (variable height with Advanced toggles) never clip */}
        <div
          className={`grid transition-all duration-200 ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
        >
          {/* SPEC-462: a closed group's rows are inert — out of the tab order. */}
          <div
            className="space-y-0.5 overflow-hidden"
            ref={(el) => { el?.toggleAttribute('inert', !isOpen); }}
          >
            {rows}
            {group.rows.length === 0 && (
              // An empty folder's line takes a dropped row too ("into", like its heading).
              <p className="px-3 py-1 text-[11px] text-[var(--text-tertiary)]" {...emptyTarget}>
                Empty folder. Use “…” on a screen to move it here.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  /** One layout group (SPEC-563). A page row OWNS its sub-tab strip in Favourites when it is
   *  starred there, otherwise in its folder (the SPEC-452 rule), so collapsing one group does
   *  not take the strip away from the other. */
  function renderLayoutGroup(group: ResolvedGroup) {
    const inFavourites = group.id === FAVOURITES_ID;
    return renderGroupShell(
      group,
      group.rows.some((r) => isPathActive(r.item.href)),
      group.rows.map((row) => {
        const edit = editing ? { group, row } : undefined;
        return row.tabKey
          ? renderSubViewRow(row, edit)
          : renderNavItem(row.item, group.id, inFavourites || !shownFavourites.includes(row.entry), edit);
      }),
    );
  }

  return (
    <div
      // Only the DESKTOP instance carries the id the rail's toggle points at (aria-controls);
      // the mobile overlay instance (which passes onNavigate) must not duplicate it.
      id={onNavigate ? undefined : 'app-sidebar'}
      ref={rootRef}
      // border-r-0 when collapsed: a 0-width panel's 1px border would double the rail's.
      // SPEC-535: w-64 (256px), was w-60. The "opens further" chevron adds a 26px slot to every
      // page row; at 240px "Data Observability" and "Method and Policy" wrapped to two lines.
      className={`flex flex-col h-full bg-[var(--sidebar-bg)] border-[var(--border-subtle)] transition-all duration-300 overflow-hidden ${collapsed ? 'w-0 border-r-0' : 'w-64 border-r'}`}
    >
      <div className="px-5 py-5 flex items-center justify-between">
        {/* SPEC-509 — the lockup is the way home: a link to Overview, named "Overview" (the
            Overview row left the list). Matters most on phones, where there is no rail. A
            login without Overview gets the plain lockup — never a dead link. */}
        <HomeLockup enabled={allowedPages.includes('/')} onNavigate={onNavigate}>
          {/* SPEC-462 — the Planning Dial mark, to the LEFT of the self-contained wordmark
              block (SPEC-459 left exactly this slot). Decorative: the link (or the h1) names it. */}
          <SuryaMark size={28} className="shrink-0 text-[var(--text-primary)]" />
          <div className="min-w-0">
          {/* SPEC-459 (scope addition) — the wordmark. Param on the live header: "I don't
              know if it's the font or something. I just don't really fuck with it."
              The font was the diagnosis. This was the ONLY lockup in the app set in
              `font-display` (Syne) — at `font-extrabold` (800), Syne's widest weight, in an
              18px slot. Syne is a quirky wide display face; at 800 the capital pair "IQ"
              and the long-tailed Q fight the lowercase, and nothing else in the product is
              set in it. Login and Invite already used DM Sans bold + tracking-tight and
              Param did not object to those, so the fix moves the sidebar TO the treatment
              he accepts rather than inventing a third.
              Two-tone, not flat amber: "IQ" carries the accent, which gives the name a
              deliberate form instead of a coloured heading — the same lockup grammar
              `StatsShell` already uses for "SuryaIQ Stats". `--text-primary` on
              `--sidebar-bg` and amber-400 on it are both far clear of AA.
              The two spans sit on ONE line on purpose: a newline between them is fine in
              JSX today, but a reformat that turned it into a space would make the
              accessible name and a copy-paste read "Surya IQ".
              SELF-CONTAINED on purpose: the h1 + subtitle live in one block, so a logo mark
              (a separate design pass) can sit to the LEFT of this block without touching
              it. Subtitle: what the platform IS (where planning happens) and what it is
              BECOMING (where the whole business plans) — one line in the 240px header.
              SPEC-462: Param CONFIRMED "Where the business runs" (03 Oct) — the platform is
              meant for the whole company, not one function. Every lockup site says it. */}
          <h1 className="text-xl font-bold tracking-tight text-[var(--text-primary)]">
            <span>Surya</span><span className="text-amber-400">IQ</span>
          </h1>
          <p className="text-xs text-[var(--text-tertiary)] mt-0.5 whitespace-nowrap">Where the business runs</p>
          </div>
        </HomeLockup>
        {onToggle && (
          <button onClick={onToggle} className="p-1.5 rounded-lg hover:bg-[var(--sidebar-hover)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors" title="Collapse sidebar">
            <PanelLeftClose className="w-4 h-4" />
          </button>
        )}
      </div>
      <nav ref={navScrollRef} className="flex-1 px-3 overflow-y-auto">
        {/* SPEC-452 — page search. Hidden while the sidebar is collapsed: a 0-width panel
            must not hold a focusable field, and the tour step anchored here then skips
            gracefully rather than spotlighting an invisible element.
            Sticky, because the nav list scrolls (26 pages) and a search box that scrolls
            away is not "at the top of the nav"; opaque background so rows pass behind it. */}
        {!collapsed && (
          <div className="mb-1 sticky top-0 z-10 bg-[var(--sidebar-bg)] py-1">
          <div className="flex items-center gap-1">
          <div className="relative flex-1 min-w-0" data-tour="nav-search">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] pointer-events-none" />
            <input
              ref={searchInputRef}
              type="search"
              value={navQuery}
              onChange={(e) => setNavQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              aria-label="Search pages"
              aria-describedby="nav-search-status"
              placeholder="Search pages…"
              className="w-full pl-8 pr-2.5 py-1.5 text-xs bg-[var(--bg-base)] border border-[var(--border-default)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:ring-1 focus:ring-amber-500/40"
            />
          </div>
          {/* SPEC-563 — the pencil: arrange this menu (desktop instance only, D10). A toggle:
              aria-pressed carries the state, the label stays stable (the star's contract). */}
          {!readOnly && (
            <button
              type="button"
              data-tour="nav-customise"
              ref={pencilRef}
              onClick={() => setEditing((v) => !v)}
              aria-pressed={editing}
              aria-label="Arrange the menu"
              title={editing ? 'Finish arranging' : 'Arrange the menu: folders, order, hide'}
              className={`flex-shrink-0 p-1.5 rounded-lg border transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-500/60 ${
                editing
                  ? 'border-amber-500/60 text-amber-400 bg-[var(--sidebar-active-bg)]'
                  : 'border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--sidebar-hover)]'
              }`}
            >
              <Pencil className="w-3.5 h-3.5" aria-hidden />
            </button>
          )}
          </div>
          {editing && !readOnly && (
            <div className="mt-1.5 p-2 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-base)] space-y-1.5">
              <p className="text-[11px] text-[var(--text-secondary)]">
                Use “…” on any screen or folder to move, hide or rename it. Right-click or Shift+F10 opens it too.
                With a mouse, you can also drag a screen or a folder to a new place.
              </p>
              {/* SPEC-563 M2c: a refused drop, in words a sighted user sees too. The aria-live
                  line below speaks it, so this copy is hidden from screen readers. */}
              {dropNote && (
                <p className="text-[11px] text-amber-400" aria-hidden>{dropNote}</p>
              )}
              <div className="flex flex-wrap gap-1">
                <button
                  type="button"
                  disabled={nav.layout.folders.length >= MAX_NAV_FOLDERS}
                  onClick={() => {
                    const id = makeFolderId(nav.layout);
                    nav.apply((l) => newFolder(l, 'New folder', id));
                    setEditStatus('Folder added at the top. Name it now.');
                    setOpenMenu(`folder|${id}`);
                    setRenaming({ id, text: 'New folder' });
                  }}
                  className="px-2 py-1 rounded text-xs bg-[var(--bg-overlay)] text-[var(--text-primary)] hover:bg-[var(--sidebar-hover)] disabled:text-[var(--text-tertiary)] disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-500/60"
                >
                  New folder
                </button>
                <button
                  type="button"
                  onClick={nav.openArrange}
                  className="px-2 py-1 rounded text-xs bg-[var(--bg-overlay)] text-[var(--text-primary)] hover:bg-[var(--sidebar-hover)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-500/60"
                >
                  Hidden screens and layouts…
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="px-2 py-1 rounded text-xs bg-amber-500 text-black hover:bg-amber-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-500/60"
                >
                  Done
                </button>
              </div>
            </div>
          )}
          </div>
        )}
        {/* SPEC-563 — what the last arrange action did, for screen readers. Mounted always
            (a live region created with its first message is routinely dropped). */}
        <p className="sr-only" aria-live="polite">{editStatus}</p>

        {/* Search status. Rendered UNCONDITIONALLY: an aria-live region created in the
            same tick as its first message is routinely dropped by screen readers, so it
            must already be mounted. Visually hidden while not searching. */}
        {/* SPEC-459 review finding 7: this used to wear the group-heading typography
            exactly — 10px, uppercase, tracking-wider, semibold — in the slot a heading
            occupies, directly above the results. That made it the one heading-SHAPED
            element in the search view, and it falsified the claim that type distinguishes
            a heading from everything else. It is a status message, not a group label, so
            it now reads as one: 11px, sentence case, normal weight. */}
        <p
          id="nav-search-status"
          aria-live="polite"
          className={`px-3 text-[11px] text-[var(--text-tertiary)] ${
            searching ? 'py-1.5' : 'sr-only'
          }`}
        >
          {searchStatus}
        </p>

        {searching ? (
          <div className="space-y-0.5">
            {/* The top hit carries a ring, so the status line's "Enter opens X" has a
                visible referent as well as a spoken one. */}
            {searchResults.map((item, i) => (
              <div key={item.href} className={i === 0 ? 'rounded-lg ring-1 ring-amber-500/50' : undefined}>
                {renderNavItem(item, 'search', false)}
                {/* SPEC-563 (D9): a screen hidden from the menu is still found, and says so. */}
                {nav.layout.hidden.includes(item.href) && (
                  <p className="flex items-center gap-2 px-3 pb-1 text-[11px] text-[var(--text-tertiary)]">
                    Hidden from your menu
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => { nav.apply((l) => show(l, item.href)); setEditStatus(`${item.name} is back in your menu.`); }}
                        aria-label={`Show ${item.name} in the menu again`}
                        className="underline text-[var(--text-secondary)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-500/60 rounded"
                      >
                        Show
                      </button>
                    )}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* SPEC-563 — the login's layout: Favourites (no pins, no group), New (screens
                filed nowhere yet), then each folder. With no stored layout this is the
                standard menu: Plan / Buy / Sell / Money / Trust / Admin. */}
            {layoutGroups.map((group) => renderLayoutGroup(group))}
          </>
        )}
      </nav>
      {/* SPEC-466: the old "AI Agents" accordion lived here. The agent findings now
          have a page of their own — /alerts, by remit — so nothing renders here. */}
      {/* SPEC-506: the "Suggest a feature" form that lived here was removed. Feedback opens
          from the bottom-right pill, Cmd/Ctrl+Shift+F, or the rail (components/feedback). */}
      {/* SPEC-462: on desktop the account (name + Sign out) lives in the rail's account
          menu; this footer stays for the MOBILE overlay sidebar, where there is no rail. */}
      <div className="px-5 py-4 border-t border-[var(--border-subtle)] md:hidden">
        {/* SPEC-509: phones have no rail, so its foot cluster lands here as labelled rows.
            Only the overlay instance (onNavigate) renders them — the desktop instance never
            shows this footer, and must not poll the Alerts feeds. */}
        {onNavigate && <RailFootRows pages={allowedPages} onNavigate={onNavigate} />}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-[var(--text-secondary)]">{user?.full_name || user?.username}</p>
            <p className="text-[10px] text-[var(--text-tertiary)]">Flying Trade Group</p>
          </div>
          <button
            onClick={logout}
            className="p-1.5 rounded-lg hover:bg-[var(--sidebar-hover)] text-[var(--text-tertiary)] hover:text-red-400 transition-colors"
            title="Sign out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
